/** Pure league-filter and ordering helpers for LiveFixturesPage (easy to test). */

import { effectiveFixtureStatus } from './liveFixtureLifecycleUi';

export const SORT_KEY_RELEVANCE = 'relevance';
export const SORT_KEY_START_TIME = 'start_time';

export const LIVE_SORT_OPTIONS = [
  { value: SORT_KEY_RELEVANCE, label: 'Relevance' },
  { value: SORT_KEY_START_TIME, label: 'Start time' },
];

export const ALL_LEAGUES = 'all';

/**
 * Relevance tier — lower sorts first.
 *
 * The window spans yesterday through two days ahead, so a plain start-time sort buries the
 * games actually being traded: yesterday's finished fixtures sit on top and anything in play
 * lands in the middle of the list. Grouping by tier first puts in-play at the top and
 * finished at the bottom, whichever direction the time sort runs.
 */
export function fixtureRelevanceTier(fixture) {
  const status = String(effectiveFixtureStatus(fixture) || '').trim().toLowerCase();
  if (status === 'complete') return 3;
  if (status === 'live') return 0;
  if (status === 'suspended') return 1;
  return fixture?.is_live_covered ? 2 : 3.5;
}

/** Epoch ms for ordering; unparseable / missing times sort last in ascending order. */
export function fixtureStartMs(fixture) {
  const raw = fixture?.start_time_utc;
  if (!raw) return Number.POSITIVE_INFINITY;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function fixtureTieBreak(fixture) {
  return String(fixture?.game_id || fixture?.gameId || fixture?.fixture_id || '');
}

/**
 * @param {'relevance'|'start_time'} sortKey
 * @param {'asc'|'desc'} sortDir Applies to the time component only — the relevance tier is
 *   never reversed, since "finished games first" is not a view anyone asked for.
 */
export function compareFixtures(a, b, { sortKey = SORT_KEY_RELEVANCE, sortDir = 'asc' } = {}) {
  if (sortKey === SORT_KEY_RELEVANCE) {
    const tierDelta = fixtureRelevanceTier(a) - fixtureRelevanceTier(b);
    if (tierDelta !== 0) return tierDelta;
  }
  const aMs = fixtureStartMs(a);
  const bMs = fixtureStartMs(b);
  if (aMs !== bMs) {
    // Infinity - Infinity is NaN, so unknown times are handled before arithmetic.
    if (!Number.isFinite(aMs)) return 1;
    if (!Number.isFinite(bMs)) return -1;
    return sortDir === 'desc' ? bMs - aMs : aMs - bMs;
  }
  return fixtureTieBreak(a).localeCompare(fixtureTieBreak(b));
}

/** Distinct non-empty leagues, alphabetical — the league filter's options. */
export function leagueOptionsFromFixtures(fixtures) {
  const seen = new Set();
  for (const f of fixtures || []) {
    const league = f?.league != null ? String(f.league).trim() : '';
    if (league) seen.add(league);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

export function fixtureMatchesLeague(fixture, league) {
  if (!league || league === ALL_LEAGUES) return true;
  return String(fixture?.league || '').trim() === league;
}

/**
 * @param {object[]} fixtures
 * @param {{league?: string, sortKey?: string, sortDir?: 'asc'|'desc'}} options
 */
export function filterAndSortFixtures(fixtures, { league, sortKey, sortDir } = {}) {
  const rows = (fixtures || []).filter((f) => fixtureMatchesLeague(f, league));
  // Copy first: the caller's array is React Query cache data and must not be sorted in place.
  return rows.slice().sort((a, b) => compareFixtures(a, b, { sortKey, sortDir }));
}
